/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';
import parameters from './__generated__/BacklogMetaAPIConsumerWrapperQuery$parameters.ts';
import type { BacklogMetaAPIConsumerWrapperQuery } from './__generated__/BacklogMetaAPIConsumerWrapperQuery.graphql.ts';
import loomCalendarsParameters from '@jira/software__backlog-app/src/loom-meeting-notes-suggestion/__generated__/useLoomConnectedCalendarsQuery$parameters.ts';
import type { useLoomConnectedCalendarsQuery } from '@jira/software__backlog-app/src/loom-meeting-notes-suggestion/__generated__/useLoomConnectedCalendarsQuery.graphql';

type NextGenBacklogEntryPointComponent = EntryPointComponent<
	{
		backlogDataQuery?: BacklogMetaAPIConsumerWrapperQuery;
		loomCalendarsQuery?: useLoomConnectedCalendarsQuery;
	},
	{},
	{},
	{}
>;

export const nextGenBacklogEntryPoint: EntryPoint<
	NextGenBacklogEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<NextGenBacklogEntryPointComponent>(
		/* webpackChunkName: "async-next-gen-backlog-entrypoint" */ './BacklogWrapper.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: (entryPointRouteParams: EntryPointRouteParams) => {
		const { context, tenantContext } = entryPointRouteParams;

		// Preload Loom calendars at route entry so the banner can read calendar data
		// synchronously during the same BacklogContent render pass as the card list.
		// Gated so ineligible users don't incur the extra network request.
		// This is independent of backlog-paginated-query — sprint data comes from Redux.
		const loomCalendarsQuery = fg('twcg-992-loom-meeting-notes-sprint-planning-gate')
			? {
					parameters: loomCalendarsParameters,
					variables: { siteId: tenantContext.cloudId },
					options: {
						fetchPolicy: 'store-or-network' as const,
					},
				}
			: undefined;

		if (!fg('backlog-paginated-query')) {
			return loomCalendarsQuery ? { queries: { loomCalendarsQuery } } : {};
		}

		const viewId = `ari:cloud:jira:${tenantContext.cloudId}:view/activation/${tenantContext.activationId}/board/${context.match.params.boardId}/backlog`;
		const rawQuickFilter = context.query?.quickFilter ?? context.query?.customFilter;
		const quickFilterIds = rawQuickFilter
			? String(rawQuickFilter).split(',').filter(Boolean).map(Number).filter(Number.isFinite)
			: undefined;

		const variables = {
			backlogViewInput: {
				jiraBacklogViewQueryInput: {
					viewId,
				},
			},
			view: {
				viewId,
			},
			...(quickFilterIds?.length ? { quickFilterIds } : {}),
		};

		const backlogDataQuery = {
			parameters,
			variables,
			options: {
				fetchPolicy: 'store-or-network' as const,
			},
		};

		return {
			queries: {
				backlogDataQuery,
				...(loomCalendarsQuery ? { loomCalendarsQuery } : {}),
			},
		};
	},
});
